import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Pagination as WVIComponentPagination } from "@sunwing/wvi-components";
import cx from "classnames";
import { Link } from "@sunwing/wvi-components";
import { A11y, Keyboard, Controller } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Icon } from "@sunwing/icons/vacances-westjet-quebec";
import { Pagination } from "./Pagination";
import { CardsContainerGrid } from "../CardsContainer/Grid";

import "swiper/css/a11y";
import "swiper/css/keyboard";
import "swiper/css/zoom";
import * as styles from "./ComponentsSlider.module.scss";

const defaultLabels = {
	firstSlideMessage: "This is the first slide",
	lastSlideMessage: "This is the last slide",
	prevSlideMessage: "Previous slide",
	nextSlideMessage: "Next slide",
	paginationBulletMessage: "Go to slide {{index}}",
	slideLabelMessage: "{{index}} of {{slidesLength}}",
};

const sliderNavigationType = {
	display: "display",
	hide: "hide",
	desktopOnly: "desktopOnly",
};

const sliderPaginationType = {
	display: "display",
	hide: "hide",
	desktopOnly: "desktopOnly",
};

const ComponentsSlider = ({
	id,
	slides,
	totalItem,
	link,
	renderLink,
	useComponentPagination,
	labels: providedLabels,
	className,
	classNames,
	slidesPerView,
	entriesPerPage,
	breakpoints,
	navigationType,
	paginationType,
}) => {
	const labels = { ...defaultLabels, ...providedLabels };
	const swiper = useRef();
	const keyboardRef = useRef(null);

	const paginationClass = cx(styles.pagination, classNames?.pagination, {
		[styles[paginationType]]: paginationType,
	});

	const enableKeyboardControl = () => {
		if (keyboardRef.current && keyboardRef.current.swiper) {
			keyboardRef.current.swiper.keyboard.enable();
		}
	};

	const disableKeyboardControl = () => {
		if (keyboardRef.current && keyboardRef.current.swiper) {
			keyboardRef.current.swiper.keyboard.disable();
		}
	};

	const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
	const [currentBreakPoint, setCurrentBreakPoint] = useState(Object.keys(breakpoints)[0]);
	const [paginationTotalIndex, setPaginationTotalIndex] = useState(
		slides.length - slidesPerView + 1
	);
	const [paginationUpdate, setPaginationUpdate] = useState(false);

	useEffect(() => {
		setPaginationTotalIndex(
			slides.length - Math.floor(breakpoints[currentBreakPoint]?.slidesPerView) + 1
		);
		setPaginationUpdate(prev => !prev);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentBreakPoint]);

	return (
		<div>
			{slides.length > 1 ? (
				<div
					className={cx(
						styles.sliderContainer,
						className,
						styles.paddingForNavigationButton,
						classNames.paddingForNavigationButton,
						{
							[styles[navigationType]]: navigationType,
						}
					)}
					onMouseEnter={enableKeyboardControl}
					onMouseLeave={disableKeyboardControl}
					onFocus={enableKeyboardControl}
					onBlur={disableKeyboardControl}>
					{slides.length > 1 && (
						<>
							<button
								key="navigationButtonPrev"
								className={cx(
									styles.navigationButton,
									styles.navigationButtonPrev,
									classNames?.navigationButton,
									classNames?.navigationButtonPrev,
									{
										[classNames?.navigationButtonDisabled]: swiper.current?.isBeginning,
										[styles[navigationType]]: navigationType,
									}
								)}
								type="button"
								aria-label={labels.prevSlideMessage}
								aria-disabled={swiper.current?.isBeginning}
								onClick={() => swiper.current.slidePrev()}
								disabled={swiper.current?.isBeginning}>
								<Icon
									name="chevron-left"
									className={cx(styles.navigationButtonIcon, classNames?.navigationButtonIcon)}
								/>
							</button>
							<button
								id={id}
								key="navigationButtonNext"
								className={cx(
									styles.navigationButton,
									styles.navigationButtonNext,
									classNames?.navigationButton,
									classNames?.navigationButtonNext,
									{
										[classNames?.navigationButtonDisabled]: swiper.current?.isEnd,
										[styles[navigationType]]: navigationType,
									}
								)}
								type="button"
								aria-label={labels.nextSlideMessage}
								aria-disabled={swiper.current?.isEnd}
								disabled={swiper.current?.isEnd}
								onClick={() => swiper.current.slideNext()}>
								<Icon
									name="chevron-right"
									className={cx(styles.navigationButtonIcon, classNames?.navigationButtonIcon)}
								/>
							</button>
						</>
					)}
					<div className={classNames?.swiperContainer}>
						<Swiper
							key={id}
							onSwiper={instance => {
								swiper.current = instance;
							}}
							ref={keyboardRef}
							className={cx(styles.swiper, styles.swiperPadding, classNames?.swiper)}
							onSlideChange={instance => {
								setCurrentSlideIndex(instance.activeIndex);
							}}
							modules={[A11y, Controller, Keyboard]}
							onBreakpoint={instance => {
								setCurrentBreakPoint(instance.currentBreakpoint);
							}}
							a11y={{
								firstSlideMessage: labels.firstSlideMessage,
								lastSlideMessage: labels.lastSlideMessage,
								prevSlideMessage: labels.prevSlideMessage,
								nextSlideMessage: labels.nextSlideMessage,
								paginationBulletMessage: labels.paginationBulletMessage,
								slideLabelMessage: labels.slideLabelMessage,
							}}
							keyboard={{ enabled: false }}
							centeredSlides={false}
							grabCursor={true}
							slidesPerView={slidesPerView}
							breakpoints={breakpoints}>
							{slides.map((slide, index) => (
								<SwiperSlide key={slide.id || index} className={styles.swiperSlide}>
									{slide.view}
								</SwiperSlide>
							))}
						</Swiper>
					</div>

					<div className={paginationClass}>
						{useComponentPagination ? (
							<WVIComponentPagination
								key={paginationUpdate}
								currentPageIndex={currentSlideIndex + 1}
								totalItems={totalItem}
								handleSlideTo={index => swiper.current.slideTo(index - 1)}
								labels={{ pagination: labels.paginationBulletMessage }}
								pageSize={entriesPerPage}
							/>
						) : (
							<Pagination
								key={paginationUpdate}
								className={cx(styles.pagination, classNames?.pagination)}
								current={currentSlideIndex}
								total={paginationTotalIndex}
								onClick={index => swiper.current.slideTo(index)}
								labels={{ pagination: labels.paginationBulletMessage }}
							/>
						)}
					</div>
				</div>
			) : (
				<>
					<CardsContainerGrid labels={labels} className={classNames?.grid}>
						{slides.map(slide => slide.view)}
					</CardsContainerGrid>
					{!!link &&
						renderLink(link, {
							children: (
								<>
									{link.text}
									<Icon name="arrow-right" />
								</>
							),
						})}
				</>
			)}
		</div>
	);
};

ComponentsSlider.propTypes = {
	id: PropTypes.string.isRequired,
	totalItem: PropTypes.number.isRequired,
	entriesPerPage: PropTypes.number.isRequired,
	useComponentPagination: PropTypes.bool,
	slides: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			view: PropTypes.node.isRequired,
		}).isRequired
	).isRequired,
	labels: PropTypes.shape({
		prevSlideLabel: PropTypes.string,
		nextSlideLabel: PropTypes.string,
		firstSlideLabel: PropTypes.string,
		lastSlideLabel: PropTypes.string,
		paginationBulletLabel: PropTypes.string,
	}),
	link: PropTypes.shape({
		to: PropTypes.string,
		text: PropTypes.string,
		target: PropTypes.string,
	}),
	renderLink: PropTypes.func,
	className: PropTypes.string,
	classNames: PropTypes.shape({
		grid: PropTypes.string,
		pagination: PropTypes.string,
		swiperContainer: PropTypes.string,
		swiper: PropTypes.string,
		navigationButton: PropTypes.string,
		navigationButtonPrev: PropTypes.string,
		navigationButtonNext: PropTypes.string,
		navigationButtonDisabled: PropTypes.string,
		navigationButtonIcon: PropTypes.string,
		footerContainer: PropTypes.string,
		paddingForNavigationButton: PropTypes.string,
	}),
	slidesPerView: PropTypes.oneOf([1, 2, 3, 4]),
	navigationType: PropTypes.oneOf(Object.keys(sliderNavigationType)).isRequired,
	paginationType: PropTypes.oneOf(Object.keys(sliderPaginationType)).isRequired,
	breakpoints: PropTypes.object,
};

ComponentsSlider.defaultProps = {
	labels: defaultLabels,
	className: undefined,
	classNames: undefined,
	useComponentPagination: false,
	link: undefined,
	renderLink: (link, { children }) => <Link href={link?.to}>{children}</Link>,
	slidesPerView: 3,
	breakpoints: {
		max: {
			slidesPerView: 1.1,
			spaceBetween: 12,
		},
		576: {
			slidesPerView: 2.2,
			spaceBetween: 12,
		},
		769: {
			slidesPerView: 3.3,
			spaceBetween: 16,
		},
	},
};

export { ComponentsSlider, sliderNavigationType, sliderPaginationType };
